import { WorkorderStatus } from "../types/workorder/workorderStatus";
import { useContext } from "react";
import { WorkorderContext } from "../context/WorkorderContext";
import { getCookie } from "../services/auth/cookies";

// Types de rôles
// TEAM_LEADER n'a volontairement aucune règle dans permissionRules : côté intervention il n'a pas
// de droit d'édition propre, et canEditField ignore un rôle sans règles. Il est déclaré ici pour
// que le module statistiques puisse le nommer sans redéclarer la liste des rôles.
export enum UserRole {
  SUPER_ADMIN = "ROLE_SUPER_ADMIN",
  ADMIN = "ROLE_ADMIN",
  TEAM_LEADER = "ROLE_TEAM_LEADER",
  PLANNER = "ROLE_PLANNER",
  TECHNICIAN = "ROLE_TECH"
}

// Champs modifiables du workorder
export enum WorkorderField {
  CUSTOMER = "customer",
  CONTACT = "contact",
  TECHNICIAN = "technician",
  ADDRESS = "address",
  SCHEDULED_START_DATE = "scheduledStartDate",
  SCHEDULED_START_TIME = "scheduledStartTime",
  SCHEDULED_END_DATE = "scheduledEndDate",
  SCHEDULED_END_TIME = "scheduledEndTime",
  ON_CALL = "onCall",
  COMP_INFO = "compInfo",
  MACHINES = "machines",
  HOSES = "hoses",
  PARTS = "parts",
  WO_START_DATE = "woStartDate",
  WO_START_TIME = "woStartTime",
  WO_END_DATE = "woEndDate",
  WO_END_TIME = "woEndTime",
  WO_DURATION = "woDuration",
  PHOTOS_BEFORE = "photosBefore",
  PHOTOS_AFTER = "photosAfter",
  SIGNATURE_AFTER = "signatureAfter",
  CONTACT_SIGNATURE_AFTER = "contactSignatureAfter",
  TRAVEL_TIME_DURATION = "travelTimeDuration",
  ON_SITE_CONTACT = "onSiteContact"
}

// Type pour définir la structure des règles de permission
type PermissionRules = {
  [status in WorkorderStatus]?: {
    [role in UserRole]?: WorkorderField[];
  };
};

// Définition des permissions par statut et rôle
const permissionRules: PermissionRules = {
  [WorkorderStatus.CREATED]: {
    [UserRole.PLANNER]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.TRAVEL_TIME_DURATION,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.ON_SITE_CONTACT,

    ],
    [UserRole.ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.TRAVEL_TIME_DURATION,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.ON_SITE_CONTACT,

    ],
    [UserRole.SUPER_ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.WO_START_DATE,
      WorkorderField.WO_START_TIME,
      WorkorderField.WO_END_DATE,
      WorkorderField.WO_END_TIME,
      WorkorderField.WO_DURATION,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ]
  },
  // Bug fix: Ajout des permissions pour ACCEPTED (TECHNICIAN, SUPER_ADMIN)
  [WorkorderStatus.ACCEPTED]: {
    [UserRole.PLANNER]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ],
    [UserRole.ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ],
    [UserRole.TECHNICIAN]: [
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER
    ],
    [UserRole.SUPER_ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.WO_START_DATE,
      WorkorderField.WO_START_TIME,
      WorkorderField.WO_END_DATE,
      WorkorderField.WO_END_TIME,
      WorkorderField.WO_DURATION,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ]
  },
  // Bug fix: Ajout des permissions pour IN_TRANSIT (TECHNICIAN, SUPER_ADMIN)
  [WorkorderStatus.IN_TRANSIT]: {
    [UserRole.PLANNER]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ],
    [UserRole.ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ],
    [UserRole.TECHNICIAN]: [
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER
    ],
    [UserRole.SUPER_ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.WO_START_DATE,
      WorkorderField.WO_START_TIME,
      WorkorderField.WO_END_DATE,
      WorkorderField.WO_END_TIME,
      WorkorderField.WO_DURATION,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ]
  },
  [WorkorderStatus.IN_PROGRESS]: {
    // Bug fix: TECHNICIAN, SUPER_ADMIN et PLANNER peuvent éditer en IN_PROGRESS
    // PLANNER peut éditer le technicien
    [UserRole.PLANNER]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ],
    [UserRole.ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ],
    [UserRole.TECHNICIAN]: [
      WorkorderField.WO_START_DATE,
      WorkorderField.WO_START_TIME,
      WorkorderField.WO_END_DATE,
      WorkorderField.WO_END_TIME,
      WorkorderField.WO_DURATION,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER
    ],
    [UserRole.SUPER_ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.WO_START_DATE,
      WorkorderField.WO_START_TIME,
      WorkorderField.WO_END_DATE,
      WorkorderField.WO_END_TIME,
      WorkorderField.WO_DURATION,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ]
  },
  [WorkorderStatus.CLOSED]: {
    // Bug fix: PLANNER peut éditer le technicien en CLOSED
    [UserRole.PLANNER]: [
      WorkorderField.ON_CALL,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.WO_START_DATE,
      WorkorderField.WO_START_TIME,
      WorkorderField.WO_END_DATE,
      WorkorderField.WO_END_TIME,
      WorkorderField.WO_DURATION,
      WorkorderField.COMP_INFO,
      WorkorderField.TRAVEL_TIME_DURATION,
      WorkorderField.ON_SITE_CONTACT
    ],
    [UserRole.ADMIN]: [
      WorkorderField.ON_CALL,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.WO_START_DATE,
      WorkorderField.WO_START_TIME,
      WorkorderField.WO_END_DATE,
      WorkorderField.WO_END_TIME,
      WorkorderField.WO_DURATION,
      WorkorderField.COMP_INFO,
      WorkorderField.TRAVEL_TIME_DURATION,
      WorkorderField.ON_SITE_CONTACT
    ],
    [UserRole.SUPER_ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.WO_START_DATE,
      WorkorderField.WO_START_TIME,
      WorkorderField.WO_END_DATE,
      WorkorderField.WO_END_TIME,
      WorkorderField.WO_DURATION,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ]
  },
  [WorkorderStatus.SUSPENDED]: {
    [UserRole.PLANNER]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ],
    [UserRole.ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ],
    [UserRole.SUPER_ADMIN]: [
      WorkorderField.CUSTOMER,
      WorkorderField.CONTACT,
      WorkorderField.TECHNICIAN,
      WorkorderField.ADDRESS,
      WorkorderField.SCHEDULED_START_DATE,
      WorkorderField.SCHEDULED_START_TIME,
      WorkorderField.SCHEDULED_END_DATE,
      WorkorderField.SCHEDULED_END_TIME,
      WorkorderField.WO_START_DATE,
      WorkorderField.WO_START_TIME,
      WorkorderField.WO_END_DATE,
      WorkorderField.WO_END_TIME,
      WorkorderField.WO_DURATION,
      WorkorderField.ON_CALL,
      WorkorderField.COMP_INFO,
      WorkorderField.MACHINES,
      WorkorderField.HOSES,
      WorkorderField.PARTS,
      WorkorderField.PHOTOS_BEFORE,
      WorkorderField.PHOTOS_AFTER,
      WorkorderField.SIGNATURE_AFTER,
      WorkorderField.CONTACT_SIGNATURE_AFTER,
      WorkorderField.ON_SITE_CONTACT
    ]
  },
  // Autres statuts sans permissions spécifiques 
  [WorkorderStatus.VALIDATED]: {},
  [WorkorderStatus.CANCELED]: {}
};

export const useWorkorderPermissions = (userRoles?: string[], explicitWorkorder?: EasyTech.Workorder) => {
  const context = useContext(WorkorderContext);
  const workorder = explicitWorkorder || context?.workorder;

  const roles = userRoles || (() => {
    const userRolesString = getCookie("userRoles") || "";
    return userRolesString.split(",").filter(role => role.trim() !== "");
  })();

  // Convertit les rôles string en enum UserRole si possible
  const normalizedRoles = roles
    .filter(role =>
      Object.values(UserRole).includes(role as UserRole) ||
      (role.startsWith("ROLE_") && Object.values(UserRole).some(userRole => userRole === role))
    ) as UserRole[];

  /**
   * Vérifie si l'utilisateur peut modifier un champ spécifique
   * @param field Le champ à vérifier
   * @returns Booléen indiquant si l'utilisateur a la permission
   */
  const canEditField = (field: WorkorderField): boolean => {
    if (!workorder || !workorder.status) return false;

    const status = workorder.status as WorkorderStatus;

    // Statuts qui verrouillent complètement l'édition pour les autres rôles
    const lockedStatuses = [
      WorkorderStatus.VALIDATED,
      WorkorderStatus.INVOICED,
      WorkorderStatus.PAID,
      WorkorderStatus.CANCELED
    ];
    if (lockedStatuses.includes(status)) {
      return false;
    }

    // Vérifier pour chaque rôle de l'utilisateur
    for (const role of normalizedRoles) {
      const rulesForStatus = permissionRules[status];
      if (!rulesForStatus) continue;

      const rulesForRole = rulesForStatus[role];
      if (!rulesForRole) continue;

      if (rulesForRole.includes(field)) {
        return true;
      }
    }

    return false;
  };

  /**
   * Vérifie si l'utilisateur peut modifier une catégorie de champs
   * @param fields Tableau des champs à vérifier
   * @returns Booléen indiquant si l'utilisateur a la permission pour au moins un des champs
   */
  const canEditAnyField = (fields: WorkorderField[]): boolean => {
    return fields.some(field => canEditField(field));
  };

  /**
   * Vérifie si l'utilisateur peut modifier tous les champs d'une catégorie
   * @param fields Tableau des champs à vérifier
   * @returns Booléen indiquant si l'utilisateur a la permission pour tous les champs
   */
  const canEditAllFields = (fields: WorkorderField[]): boolean => {
    return fields.every(field => canEditField(field));
  };

  /**
   * Obtient tous les champs que l'utilisateur peut modifier
   * @returns Tableau des champs modifiables
   */
  const getEditableFields = (): WorkorderField[] => {
    if (!workorder || !workorder.status) return [];

    const editableFields: WorkorderField[] = [];

    // Parcourir tous les champs possibles
    Object.values(WorkorderField).forEach(field => {
      if (canEditField(field as WorkorderField)) {
        editableFields.push(field as WorkorderField);
      }
    });

    return editableFields;
  };

  return {
    canEditField,
    canEditAnyField,
    canEditAllFields,
    getEditableFields
  };
}; 
